import { useSearchParams } from "react-router-dom";
import { useState, useCallback, useMemo, useEffect, useRef } from "react";
import { formatISO, isValid, parseISO } from "date-fns";
import type { FilterState } from "@/types/dashboard";
import {
  dashboardQueryFiltersSchema,
  dashboardQueryFiltersToSearchParams,
  hasActiveDashboardQueryFilters,
  normalizeStoredDashboardFilters,
} from "@/types/dashboard";

const LAST_FILTERS_STORAGE_KEY = "dashboard-last-filters";
const DEFAULT_PAGE = 1;
const DEFAULT_LIMIT = 15;

const parsePageParam = (raw: string | null): number => {
  const parsed = Number(raw ?? DEFAULT_PAGE);
  return Number.isFinite(parsed) ? Math.max(1, Math.trunc(parsed)) : DEFAULT_PAGE;
};

const parseLimitParam = (raw: string | null): number => {
  const parsed = Number(raw ?? DEFAULT_LIMIT);
  return Number.isFinite(parsed) ? Math.max(1, Math.trunc(parsed)) : DEFAULT_LIMIT;
};

const parseDateParam = (param: string | null): Date | undefined => {
  if (!param) return undefined;
  const d = parseISO(param);
  return isValid(d) ? d : undefined;
};

export const useDashboardFilters = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const [filtersOpen, setFiltersOpen] = useState(false);
  const restoredFromStorageRef = useRef(false);

  useEffect(() => {
    if (restoredFromStorageRef.current) return;
    if (searchParams.toString() !== "") return;

    restoredFromStorageRef.current = true;

    try {
      const savedData = localStorage.getItem(LAST_FILTERS_STORAGE_KEY);
      if (!savedData) return;

      const parsed: unknown = JSON.parse(savedData);
      const validated = dashboardQueryFiltersSchema.safeParse(
        normalizeStoredDashboardFilters(parsed),
      );

      if (!validated.success || !hasActiveDashboardQueryFilters(validated.data)) {
        return;
      }

      const params = dashboardQueryFiltersToSearchParams(validated.data);

      if (
        parsed &&
        typeof parsed === "object" &&
        "datePreset" in parsed &&
        typeof (parsed as { datePreset: unknown }).datePreset === "string"
      ) {
        params.set("preset", (parsed as { datePreset: string }).datePreset);
      }

      setSearchParams(params, { replace: true });
    } catch (e) {
      console.warn("Error reading/parsing filters from localStorage", e);
    }
  }, [searchParams, setSearchParams]);

  const initialFilters = useMemo(() => {
    const parseUrlArray = (param: string | null) => (param ? param.split(",") : []);
    
    return {
      geo: parseUrlArray(searchParams.get("geo")),
      siteTypes: parseUrlArray(searchParams.get("siteTypes")),
      status: parseUrlArray(searchParams.get("status")),
      languages: parseUrlArray(searchParams.get("languages")),
      keywords: parseUrlArray(searchParams.get("keywords")),
      dateRange: {
        from: parseDateParam(searchParams.get("from")),
        to: parseDateParam(searchParams.get("to")),
      },
      datePreset: searchParams.get("preset") || null,
    };
  }, [searchParams]);

  const handleApplyFilters = useCallback((filters: FilterState, closePanel = false) => {
    const newParams = new URLSearchParams(searchParams);

    const setOrDelete = (key: string, values: string[]) => {
      if (values?.length > 0) newParams.set(key, values.join(","));
      else newParams.delete(key);
    };

    setOrDelete("geo", filters.geo);
    setOrDelete("siteTypes", filters.siteTypes);
    setOrDelete("status", filters.status);
    setOrDelete("languages", filters.languages);
    setOrDelete("keywords", filters.keywords);

    if (filters.dateRange.from) newParams.set("from", formatISO(filters.dateRange.from));
    else newParams.delete("from");

    if (filters.dateRange.to) newParams.set("to", formatISO(filters.dateRange.to));
    else newParams.delete("to");

    if (filters.datePreset) newParams.set("preset", filters.datePreset);
    else newParams.delete("preset");

    newParams.set("page", "1");
    setSearchParams(newParams);
    
    if (closePanel) setFiltersOpen(false);

    try {
      localStorage.setItem(LAST_FILTERS_STORAGE_KEY, JSON.stringify(filters));
    } catch (e) {
      console.warn("Failed to save filters to localStorage", e);
    }
  }, [searchParams, setSearchParams]);

  const handlePageChange = useCallback((newPage: number) => {
    const newParams = new URLSearchParams(searchParams);
    newParams.set("page", newPage.toString());
    setSearchParams(newParams);
  }, [searchParams, setSearchParams]);

  const handleLimitChange = useCallback(
    (newLimit: string) => {
      const newParams = new URLSearchParams(searchParams);
      newParams.set("limit", newLimit);
      newParams.set("page", "1");
      setSearchParams(newParams);
    },
    [searchParams, setSearchParams],
  );

  return {
    initialFilters,
    filtersOpen,
    setFiltersOpen,
    handleApplyFilters,
    handlePageChange,
    handleLimitChange,
    page: parsePageParam(searchParams.get("page")),
    limit: parseLimitParam(searchParams.get("limit")),
  };
};